import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';

import { SignalRService } from './singal-r.service';
import { OnboardingService } from './api/onboarding.service';
import { selectToken } from '../../store/selectors/token.selectors';

/**
 * Centralized post-MFA initialization service.
 *
 * Orchestrates shared app startup tasks that must run **once** after
 * authentication succeeds (SignalR connection, countries prefetch, etc.).
 * Guards and components can await `ensureInitialized()` to block until
 * all bootstrap work is done.
 */
@Injectable({ providedIn: 'root' })
export class AppInitializationService implements OnDestroy {
  /** Emits `true` while initialization tasks are in-flight. */
  readonly initializing$ = new BehaviorSubject<boolean>(false);

  private _initialized = false;
  private _initPromise: Promise<void> | null = null;
  private _currentToken = '';
  private _tokenSub: Subscription | null = null;

  constructor(
    private readonly store: Store,
    private readonly signalRService: SignalRService,
    private readonly onboardingService: OnboardingService,
  ) {}

  /** Whether initialization has already completed successfully. */
  get initialized(): boolean {
    return this._initialized;
  }

  /**
   * Run all post-MFA startup tasks. Idempotent — subsequent calls return
   * the same promise / resolve immediately if already done.
   */
  ensureInitialized(): Promise<void> {
    if (this._initialized) return Promise.resolve();
    if (this._initPromise) return this._initPromise;

    this._initPromise = this._runInitialization();
    return this._initPromise;
  }

  /**
   * Reset state (e.g. on logout). Stops SignalR and allows
   * re-initialization on next login.
   */
  reset(): void {
    this._initialized = false;
    this._initPromise = null;
    this._tokenSub?.unsubscribe();
    this._tokenSub = null;
    this.signalRService.stopConnection();
    this.initializing$.next(false);
  }

  ngOnDestroy(): void {
    this._tokenSub?.unsubscribe();
  }

  // ─── Private ────────────────────────────────────────────────

  private async _runInitialization(): Promise<void> {
    this.initializing$.next(true);

    try {
      // Keep a live reference to the latest token so SignalR's
      // reconnect logic always gets a fresh value.
      this._subscribeToToken();

      // Wait for the first token to be available in the store.
      await firstValueFrom(
        this.store.select(selectToken).pipe(
          filter((t): t is string => !!t),
          take(1),
        ),
      );

      // Run startup tasks in parallel.
      await Promise.all([
        this._startSignalR(),
        this._prefetchCountries(),
      ]);

      this._initialized = true;
    } catch (err) {
      console.error('[AppInit] Initialization failed:', err);
      // Mark as initialized anyway so the app is not blocked forever.
      this._initialized = true;
    } finally {
      this.initializing$.next(false);
    }
  }

  /** Subscribe to token changes so `_currentToken` is always fresh. */
  private _subscribeToToken(): void {
    if (this._tokenSub) return;

    this._tokenSub = this.store
      .select(selectToken)
      .pipe(
        filter((t): t is string => !!t),
        distinctUntilChanged(),
      )
      .subscribe((token) => {
        this._currentToken = token;
      });
  }

  private async _startSignalR(): Promise<void> {
    await this.signalRService.startConnection(() => this._currentToken);
  }

  private _prefetchCountries(): Promise<void> {
    return firstValueFrom(this.onboardingService.getCountries()).then(
      () => {},
    );
  }
}
